import { ApiClient, ApiError, ApiUnreachableError } from '@accounting/shared';

/**
 * The browser's view of the API.
 *
 * `ApiClient` is the same class the CLI and the MCP server use — the error
 * envelope is parsed in one place, so a refusal reads the same wherever it is
 * shown. What differs here is `credentials: 'include'`: the Better Auth session
 * is a cookie and the UI is a different origin from the API, so without it the
 * browser sends session requests unauthenticated.
 */

export { ApiError, ApiUnreachableError };

/**
 * Where the API is.
 *
 * Vite inlines this at build time, which is why the compose file passes it as a
 * build argument rather than an environment variable — see apps/ui/Dockerfile.
 *
 * Dot access is required: Vite substitutes `import.meta.env.VITE_X` and leaves
 * `import.meta.env['VITE_X']` alone, so bracket access would make the build
 * argument a no-op and every deployment would talk to localhost. The property is
 * declared in vite-env.d.ts so this typechecks under
 * noPropertyAccessFromIndexSignature.
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/** A client for the routes authenticated by the Better Auth session cookie. */
export function sessionClient(): ApiClient {
  return new ApiClient({ baseUrl: API_BASE_URL, credentials: 'include' });
}

/** A client for `/v1`, which requires a company access token. */
export function companyClient(token: string): ApiClient {
  return new ApiClient({ baseUrl: API_BASE_URL, token, credentials: 'include' });
}

/**
 * Sign in through Better Auth.
 *
 * Not an `ApiClient` call: Better Auth owns `/api/auth/*` and answers in its
 * own shape rather than the API's error envelope, so treating it as one would
 * mean reporting "unknown_error" for a wrong password.
 */
export interface SignInResult {
  readonly twoFactorRequired: boolean;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/auth/sign-in/email`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
  } catch (cause) {
    // The request never arrived. Nothing has been said about the credentials.
    throw new ApiUnreachableError(API_BASE_URL, cause);
  }

  const body = (await response.json().catch(() => null)) as {
    message?: string;
    twoFactorRedirect?: boolean;
    request_id?: string;
  } | null;

  if (!response.ok) {
    /*
      Only a 401 or 403 is about the credentials.

      This used to answer "Forkert e-mail eller adgangskode" for *any* failure,
      including a 500 — so with the database down, the screen told people their
      password was wrong and they retyped a password that was correct. It cost
      an afternoon once, twice in the same day.

      Better Auth answers 401 for both an unknown address and a wrong password,
      and that part is deliberate: distinguishing them would disclose which
      email addresses have accounts here.
    */
    if (response.status === 401 || response.status === 403) {
      throw new Error(body?.message ?? 'Forkert e-mail eller adgangskode.');
    }

    const reference =
      body?.request_id === undefined ? '' : ` Reference: ${body.request_id}.`;
    throw new Error(
      body?.message ??
        `Serveren svarede ${String(response.status)} ved login. Det er ikke en ` +
          `fejl i din e-mail eller adgangskode.${reference}`,
    );
  }

  return { twoFactorRequired: body?.twoFactorRedirect === true };
}

/** Verify a TOTP code, completing a sign-in that asked for a second factor. */
export async function verifyTotp(code: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/auth/two-factor/verify-totp`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? 'Koden blev ikke godkendt.');
  }
}

/**
 * End the Better Auth session.
 *
 * **Resolves only once the server has ended it.** The session cookie is
 * httpOnly, so nothing in the browser can remove it: until the server answers
 * 2xx the person is still signed in, and a caller that showed a login screen
 * anyway would be telling them otherwise on a machine where they are not.
 *
 * This shipped broken in both halves. The request carried no `content-type`,
 * which Better Auth refuses with 415 UNSUPPORTED_MEDIA_TYPE, and the response
 * was never read — so the UI went to /login, the cookie stayed valid, and
 * deleting `/login` from the address bar signed the person straight back in.
 */
export async function signOut(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/auth/sign-out`, {
      method: 'POST',
      // Required, not decorative: Better Auth accepts only application/json on
      // a POST and answers 415 to a request that names no type at all.
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: '{}',
    });
  } catch (cause) {
    throw new ApiUnreachableError(API_BASE_URL, cause);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(
      body?.message ??
        `Serveren svarede ${String(response.status)}, og sessionen er ikke afsluttet.`,
    );
  }
}
